import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, Trash2, Mail, AlertOctagon, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MasciLogo } from "@/components/MasciLogo";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateLong } from "@/lib/utils";
import { printReport, maybeAutoPrint } from "@/lib/printReport";
import { PrintWatermark } from "@/components/PrintWatermark";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { PhotoZipDownload } from "@/components/PhotoZipDownload";
import { EmailReportDialog } from "@/components/EmailReportDialog";
import { SubmitLangBadge } from "@/components/SubmitLangBadge";
import ShopSignoffCard from "@/components/ShopSignoffCard";
import { itemSeverity } from "@/lib/equipmentSeverity";
import { isAdmin } from "@/lib/adminAuth";
import { useT } from "@/lib/i18n";

const KV = ({ label, value, full = false }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
      {label}
    </div>
    <div className="text-base text-slate-900 mt-1 whitespace-pre-wrap">
      {value || "—"}
    </div>
  </div>
);

const StatusPill = ({ status }) => {
  const { t } = useT();
  const map = {
    pass: { cls: "bg-emerald-600 text-white", label: t("PASS") },
    fail: { cls: "bg-red-700 text-white", label: t("FAIL") },
    na: { cls: "bg-slate-500 text-white", label: t("N/A") },
  };
  const v = map[status] || { cls: "bg-slate-300 text-slate-700", label: "—" };
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-mono font-black tracking-[0.1em] ${v.cls}`}>
      {v.label}
    </span>
  );
};

export default function ViewEquipmentInspection({ context = "admin" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailOpen, setEmailOpen] = useState(false);

  const isShopContext = context === "shop";
  const backHref = isShopContext ? "/shop" : "/admin/equipment";
  const headerAccent = isShopContext ? "border-amber-500" : "border-red-700";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/equipment-inspections/${id}`);
        if (alive) setData(res.data);
      } catch {
        toast.error(t("Inspection not found"));
        navigate(backHref);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, navigate]); // eslint-disable-line

  useEffect(() => {
    if (!loading && data) maybeAutoPrint();
  }, [loading, data]);

  const onDelete = async () => {
    if (!window.confirm(t("Permanently delete this equipment inspection?"))) return;
    try {
      await api.delete(`/equipment-inspections/${id}`);
      toast.success(t("Deleted"));
      navigate(backHref);
    } catch {
      toast.error(t("Could not delete"));
    }
  };

  // Update a single signoff entry in local state so the UI reflects the
  // new state without a full reload.
  const updateSignoff = (section, item, signoffEntry) => {
    setData((prev) => {
      if (!prev) return prev;
      const key = `${section}|${item}`;
      const current = (prev.shop_signoffs || []).filter((s) => s.key !== key);
      const next = signoffEntry ? [...current, signoffEntry] : current;
      return { ...prev, shop_signoffs: next };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-red-700" />
      </div>
    );
  }
  if (!data) return null;

  const fail = (data.fail_count || 0) > 0;
  const signoffsByKey = Object.fromEntries(
    (data.shop_signoffs || []).map((s) => [s.key, s])
  );

  return (
    <div className="min-h-screen bg-slate-50 print:bg-white pb-32 print:pb-0">
      <PrintWatermark />
      <div className="caution-stripe print:hidden" />

      <header className={`bg-slate-900 border-b-4 ${headerAccent} print:hidden`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link
            to={backHref}
            className="inline-flex items-center text-white hover:text-red-300 text-sm font-bold uppercase tracking-wide"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> {isShopContext ? t("Shop") : t("All Inspections")}
          </Link>
          <MasciLogo variant="mark" size="md" homeLink={isShopContext ? "/shop" : "/admin"} />
          <div className="flex items-center gap-2">
            <Button onClick={() => setEmailOpen(true)} className="h-10 px-3 bg-slate-700 hover:bg-slate-800 text-white font-bold uppercase tracking-wide text-xs" data-testid="email-btn">
              <Mail className="w-4 h-4 mr-1" /> {t("Email")}
            </Button>
            <Button onClick={() => printReport()} className="h-10 px-3 bg-red-700 hover:bg-red-800 text-white font-bold uppercase tracking-wide text-xs" data-testid="print-btn">
              <Printer className="w-4 h-4 mr-1" /> {t("Print")}
            </Button>
            {isAdmin() && (
              <Button onClick={onDelete} variant="outline" className="h-10 px-3 border-2 border-red-700 text-red-700 hover:bg-red-50 font-bold uppercase tracking-wide text-xs" data-testid="delete-btn">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 print:py-0 space-y-5">
        <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-red-700">Equipment Pre-Op Inspection</span>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900 mt-1">
                {data.equipment_type} · {data.equipment_unit}
              </h1>
              <div className="text-sm text-slate-600 mt-2">
                {formatDateLong(data.inspection_date)} · {data.inspection_time} · {data.location}
              </div>
              {data.submit_language === "es" && (
                <div className="mt-2">
                  <SubmitLangBadge lang={data.submit_language} />
                </div>
              )}
            </div>
            {fail && (
              <div className="bg-red-50 border-2 border-red-700 rounded px-4 py-2 flex items-center gap-2">
                <AlertOctagon className="w-5 h-5 text-red-700" />
                <span className="font-display font-black text-red-700 text-sm uppercase tracking-wide">
                  Fail — Out of Service
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
          <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">Project & Operator</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KV label="Project" value={data.project_name} />
            <KV label="Project #" value={data.project_number} />
            <KV label="Location" value={data.location} full />
            <KV label="Operator" value={data.operator_name} />
            <KV label="Date / Time" value={`${data.inspection_date} ${data.inspection_time}`} />
          </div>
        </div>

        <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
          <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">Equipment</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <KV label="Type" value={data.equipment_type} />
            <KV label="Unit" value={data.equipment_unit} />
            <KV label="Make" value={data.equipment_make} />
            <KV label="Model" value={data.equipment_model} />
            <KV label="Serial #" value={data.equipment_serial} />
            <KV label="Hour Meter / Odometer" value={data.hour_meter || data.odometer} />
          </div>
        </div>

        {/* Tally */}
        <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
          <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">{t("Inspection Summary")}</h2>
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-center">
              <div className="font-display text-3xl font-black text-emerald-700" data-testid="view-pass-count">{data.pass_count || 0}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{t("Pass")}</div>
            </div>
            <div className="text-center">
              <div className="font-display text-3xl font-black text-red-700" data-testid="view-fail-count">{data.fail_count || 0}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{t("Fail")}</div>
            </div>
            <div className="text-center">
              <div className="font-display text-3xl font-black text-slate-600" data-testid="view-na-count">{data.na_count || 0}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1">{t("N/A")}</div>
            </div>
            <div className="ml-auto">
              <div className={`px-4 py-2 rounded font-mono text-xs font-black uppercase tracking-[0.2em] ${
                fail ? "bg-red-700 text-white" : "bg-emerald-600 text-white"
              }`}>
                {fail ? "Out of Service" : "Cleared to Operate"}
              </div>
            </div>
          </div>
        </div>

        {/* Checklist sections */}
        {Object.entries(data.checklist || {}).map(([sectionTitle, items]) => (
          <div key={sectionTitle} className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
            <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">{sectionTitle}</h2>
            <div className="space-y-2">
              {Object.entries(items).map(([item, res]) => {
                const isFail = res?.status === "fail";
                const sev = isFail ? itemSeverity(item) : null;
                const key = `${sectionTitle}|${item}`;
                const existing = signoffsByKey[key] || null;
                return (
                  <div key={item} className={`py-1.5 border-b border-slate-100 last:border-0 ${
                    isFail
                      ? sev === "oos"
                        ? "border-l-4 border-l-red-700 pl-3"
                        : "border-l-4 border-l-amber-500 pl-3"
                      : ""
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 text-sm text-slate-800">
                        {item}
                        {isFail && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono font-black tracking-[0.1em] align-middle"
                            style={sev === "oos" ? { background: "#b91c1c", color: "white" } : { background: "#f59e0b", color: "white" }}>
                            {sev === "oos" ? <><AlertOctagon className="w-2.5 h-2.5" /> {t("OOS")}</> : <><AlertTriangle className="w-2.5 h-2.5" /> {t("ATTN")}</>}
                          </span>
                        )}
                        {res?.note && (
                          <div className="text-xs text-slate-500 italic mt-0.5">↳ {res.note}</div>
                        )}
                        {res?.photo && (
                          <PhotoLightbox
                            src={res.photo}
                            alt={`Failure evidence — ${item}`}
                            filename={`ForgedOps_EquipFail_${(data.id || id || "").slice(0, 8)}_${item.replace(/[^A-Za-z0-9]+/g, "_").slice(0, 30)}.jpg`}
                            className="mt-2 inline-block"
                            testId={`equip-failphoto-${item}`}
                          >
                            <img
                              src={res.photo}
                              alt="Failure evidence"
                              className="w-32 h-24 object-cover rounded border-2 border-red-300"
                            />
                          </PhotoLightbox>
                        )}
                      </div>
                      <StatusPill status={res?.status} />
                    </div>
                    {isFail && (
                      <ShopSignoffCard
                        inspectionId={data.id || id}
                        section={sectionTitle}
                        item={item}
                        severity={sev}
                        existing={existing}
                        onChange={(entry) => updateSignoff(sectionTitle, item, entry)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {(data.deficiency_notes || data.corrective_actions) && (
          <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
            <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">Notes & Corrective Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <KV label="Deficiencies" value={data.deficiency_notes} />
              <KV label="Corrective Actions" value={data.corrective_actions} />
            </div>
          </div>
        )}

        {data.photos && data.photos.length > 0 && (
          <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
            <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-slate-200">
              <h2 className="font-display text-xl font-black text-slate-900">Photos ({data.photos.length})</h2>
              <PhotoZipDownload
                photos={data.photos}
                prefix={`ForgedOps_Equipment_${(data.id || id || "").slice(0, 8)}_photos`}
                testId="equipment-photos-zip"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {data.photos.map((p, i) => (
                <PhotoLightbox
                  key={i}
                  src={p}
                  alt={`Equipment Photo ${i + 1}`}
                  filename={`ForgedOps_Equipment_${(data.id || id || "").slice(0, 8)}_photo${i + 1}.jpg`}
                  className="block w-full"
                  testId={`equip-photo-${i}`}
                >
                  <img src={p} alt={`Photo ${i + 1}`} className="w-full aspect-[4/3] object-cover rounded border border-slate-200" />
                </PhotoLightbox>
              ))}
            </div>
          </div>
        )}

        {data.operator_signature && (
          <div className="bg-white border-2 border-slate-300 rounded-md p-5 sm:p-7 print-section">
            <h2 className="font-display text-xl font-black text-slate-900 mb-4 pb-2 border-b-2 border-slate-200">Sign-Off</h2>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Operator: {data.operator_name}</div>
            <img src={data.operator_signature} alt="Operator signature" className="max-h-32 border-b-2 border-slate-300" />
          </div>
        )}
      </main>

      <EmailReportDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        kind="equipment-inspection"
        recordId={id}
        record={data}
      />
    </div>
  );
}
